import React, { useEffect, useRef, useState } from 'react';
import { Col, Form, Input, Row, Select, theme } from 'antd';
import { EnumDeviceSearchType, IDeviceSearchParams } from '../../../common/models';
import { getEnumEntries } from '../../../common/utils';

interface IDeviceSearchFormProps {
  searchType?: EnumDeviceSearchType;
  keywords?: string;
  customers: any[];
  onSearch: (searchParams: IDeviceSearchParams) => void;
}

const DeviceSearchForm: React.FC<IDeviceSearchFormProps> = ({
  searchType,
  keywords,
  customers,
  onSearch,
}) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [currSearchType, setCurrSearchType] = useState(searchType ?? EnumDeviceSearchType.customerName);
  const onSearchRef = useRef(onSearch);
  const currSearchTypeRef = useRef(currSearchType);
  const keywordsRef = useRef(keywords);

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    const currType = currSearchTypeRef.current;
    const kw = keywordsRef.current;
    if (customers?.length > 0 && currType === EnumDeviceSearchType.customerName && !kw) {
      const firstCustomerName = customers[0].Name;
      form.setFieldsValue({ keywords: firstCustomerName });
      onSearchRef.current({ searchType: currType, ...form.getFieldsValue() });
    }
  }, [form, customers]);

  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
    borderRadius: token.borderRadiusLG,
  };

  const handleSearchTypeChange = (newSearchType: EnumDeviceSearchType) => {
    form.setFieldsValue({ keywords: '' });
    setCurrSearchType(newSearchType);
  };

  const getFields = () => {
    const fields = [
      <Col
        xs={24}
        sm={24}
        md={6}
        lg={6}
        key='type'
        style={{ padding: 10}}
      >
        <Select
          defaultValue={currSearchType}
          options={getEnumEntries(EnumDeviceSearchType)}
          onChange={handleSearchTypeChange}
          style={{ width: '100%' }}
        />
      </Col>,
      <Col
        xs={24}
        sm={24}
        md={18}
        lg={18}
        key='search'
        style={{ padding: 10}}
      >
          {
            currSearchType === EnumDeviceSearchType.customerName ? (
              <Form.Item
                name='keywords'
                initialValue=''
                style={{ flex: 1, marginBottom: 0 }}
                rules={[{ required: true, message: `Please input and select ${searchType}` }]}
              >
                <Select
                  showSearch
                  placeholder="Search to Select"
                  optionFilterProp="label"
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  options={customers.map((customer) => ({
                    value: customer.Name,
                    label: customer.Name,
                  }))}
                  style={{ flex: 1 }}
                  onChange={(value) => onSearch({ searchType: currSearchType, keywords: value })}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name='keywords'
                initialValue={keywords}
                style={{ flex: 1, marginBottom: 0 }}
                rules={[{ required: true, message: `Please input ${currSearchType}` }]}
              >
                <Input.Search
                  allowClear
                  enterButton
                  onSearch={() => onSearch({ searchType: currSearchType, ...form.getFieldsValue() })}
                />
              </Form.Item>
            )
          }
      </Col>,
    ];
    return fields;
  };

  return (
    <Form
      form={form}
      name="advanced_search"
      style={formStyle}
    >
      <Row gutter={24} wrap style={{ marginLeft: 0, marginRight: 0 }}>{getFields()}</Row>
    </Form>
  );
};

export default DeviceSearchForm;